import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "@/api/axiosInstance";

// Product interfaces based on backend models
export interface Material {
    id: number;
    name: string;
    description?: string;
    additional_cost: number;
    is_active: boolean;
}

export interface Color {
    id: number;
    name: string;
    hex_code: string;
    additional_cost: number;
    is_active: boolean;
}

export interface Size {
    id: number;
    name: string;
    description?: string;
    additional_cost: number;
    is_active: boolean;
    sort_order: number;
}

export interface CustomizationOption {
    id: number;
    name: string;
    description: string;
    additional_cost: number;
    is_active: boolean;
    sort_order: number;
}

export interface Category {
    id: number;
    name: string;
    slug: string;
    description?: string;
    parent?: number;
    image_url?: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface BulkPricingTier {
    id: number;
    min_quantity: number;
    max_quantity?: number;
    discount_percentage: number;
}

export interface Product {
    id: number;
    name: string;
    slug: string;
    description: string;
    short_description?: string;
    category: Category;
    base_price: number;
    min_price: number;
    max_price: number;
    main_image_url: string;
    gallery_images: string[];
    weight?: number;
    dimensions?: string;
    is_active: boolean;
    is_featured: boolean;
    stock_quantity: number;
    min_order_quantity: number;
    allows_text_customization: boolean;
    allows_logo_upload: boolean;
    allows_pattern_upload: boolean;
    max_text_length: number;
    available_materials: Material[];
    available_colors: Color[];
    available_sizes: Size[];
    available_customizations: CustomizationOption[];
    bulk_pricing: BulkPricingTier[];
    meta_title?: string;
    meta_description?: string;
    created: string;
    updated: string;
}

export interface ProductListParams {
    page?: number;
    page_size?: number;
    search?: string;
    category?: number;
    min_price?: number;
    max_price?: number;
    materials?: number[];
    colors?: number[];
    sizes?: number[];
    ordering?: string;
    is_featured?: boolean;
}

export interface ProductListResponse {
    count: number;
    next?: string;
    previous?: string;
    results: Product[];
}

// Fetch products with filtering and pagination
const fetchProducts = async (params: ProductListParams = {}): Promise<ProductListResponse> => {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
                value.forEach(v => searchParams.append(key, v.toString()));
            } else {
                searchParams.append(key, value.toString());
            }
        }
    });

    const { data } = await axiosInstance.get(`/products/?${searchParams.toString()}`);
    return data;
};

// Fetch single product by ID
const fetchProduct = async (id: number): Promise<Product> => {
    const { data } = await axiosInstance.get(`/products/${id}/`);
    return data;
};

// Fetch categories
const fetchCategories = async (): Promise<Category[]> => {
    const { data } = await axiosInstance.get("/products/categories/");
    return data.results || data;
};

// Fetch materials
const fetchMaterials = async (): Promise<Material[]> => {
    const { data } = await axiosInstance.get("/products/materials/");
    return data.results || data;
};

// Fetch colors
const fetchColors = async (): Promise<Color[]> => {
    const { data } = await axiosInstance.get("/products/colors/");
    return data.results || data;
};

// Fetch sizes
const fetchSizes = async (): Promise<Size[]> => {
    const { data } = await axiosInstance.get("/products/sizes/");
    return data.results || data;
};

// Hook for fetching products with filters
export const useProducts = (params: ProductListParams = {}) => {
    return useQuery({
        queryKey: ["products", params],
        queryFn: () => fetchProducts(params),
        staleTime: 2 * 60 * 1000, // 2 minutes
        gcTime: 5 * 60 * 1000, // 5 minutes
    });
};

// Hook for fetching single product
export const useProduct = (id: number) => {
    return useQuery({
        queryKey: ["product", id],
        queryFn: () => fetchProduct(id),
        enabled: !!id,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
    });
};

// Hook for fetching categories
export const useCategories = () => {
    return useQuery({
        queryKey: ["categories"],
        queryFn: fetchCategories,
        staleTime: 10 * 60 * 1000, // 10 minutes
        gcTime: 30 * 60 * 1000, // 30 minutes
    });
};

// Hook for fetching materials
export const useMaterials = () => {
    return useQuery({
        queryKey: ["materials"],
        queryFn: fetchMaterials,
        staleTime: 10 * 60 * 1000, // 10 minutes
        gcTime: 30 * 60 * 1000, // 30 minutes
    });
};

// Hook for fetching colors
export const useColors = () => {
    return useQuery({
        queryKey: ["colors"],
        queryFn: fetchColors,
        staleTime: 10 * 60 * 1000, // 10 minutes
        gcTime: 30 * 60 * 1000, // 30 minutes
    });
};

// Hook for fetching sizes
export const useSizes = () => {
    return useQuery({
        queryKey: ["sizes"],
        queryFn: fetchSizes,
        staleTime: 10 * 60 * 1000, // 10 minutes
        gcTime: 30 * 60 * 1000, // 30 minutes
    });
};

// Admin hooks for managing products (requires staff permissions)
export const useCreateProduct = () => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: async (productData: Partial<Product>) => {
            const { data } = await axiosInstance.post("/products/", productData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["products"] });
        },
    });
};

export const useUpdateProduct = () => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: async ({ id, ...productData }: Partial<Product> & { id: number }) => {
            const { data } = await axiosInstance.patch(`/products/${id}/`, productData);
            return data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ["products"] });
            queryClient.invalidateQueries({ queryKey: ["product", data.id] });
        },
    });
};

export const useDeleteProduct = () => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: async (id: number) => {
            await axiosInstance.delete(`/products/${id}/`);
            return id;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["products"] });
        },
    });
};
